import { useState } from "react";
import { useSubscription } from "../../context/SubscriptionContext";
// Environment variables loaded from .env
import { createCheckoutSession } from "../../api/subscription";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "../ui/alert-dialog";
import { Users, ArrowUpRight } from "lucide-react";

/**
 * MaxSeatModal — shown when a user attempts to invite beyond the plan seat limit
 * (e.g. a 3rd supporting user on the monthly plan).
 */
export default function MaxSeatModal({ open, onOpenChange }) {
  const { maxSeats } = useSubscription();

  const [isUpgrading, setIsUpgrading] = useState(false);

  const handleUpgrade = async () => {
    try {
      setIsUpgrading(true);
      const response = await createCheckoutSession({ targetPlan: "clinic" });
      const checkoutUrl = response?.url || response?.checkoutUrl || response?.checkout_url;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        window.open(process.env.REACT_APP_CLINIC_ONBOARDING_URL, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.warn("Checkout session API error (fallback to onboarding URL):", error);
      window.open(process.env.REACT_APP_CLINIC_ONBOARDING_URL, "_blank", "noopener,noreferrer");
    } finally {
      setIsUpgrading(false);
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 mb-2">
            <Users className="h-7 w-7 text-amber-600" />
          </div>

          <AlertDialogTitle className="text-center text-lg font-bold text-gray-900">
            User Limit Reached
          </AlertDialogTitle>

          <AlertDialogDescription className="text-center text-sm text-gray-600 mt-2 leading-relaxed">
            You have reached the maximum of {maxSeats} users for your current plan.
            Upgrade your clinic subscription to add more team members.
          </AlertDialogDescription>

          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-3 py-1.5">
              <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-semibold text-red-700">
                {maxSeats}/{maxSeats} seats used
              </span>
            </div>
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter className="mt-4 sm:flex-row gap-2">
          <AlertDialogCancel className="flex-1">Close</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleUpgrade}
            disabled={isUpgrading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            <ArrowUpRight className="h-4 w-4" />
            {isUpgrading ? "Redirecting..." : "Upgrade Plan"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
